import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './PortalSetPassword.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const PortalSetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('No invitation token provided');
        setValidatingToken(false);
        return;
      }

      try {
        // Check if token is valid by calling validate endpoint
        const response = await axios.post(`${API_BASE_URL}/portal/password/validate_token/`, {
          token
        });
        setTokenValid(true);
      } catch (err: any) {
        // If no validate endpoint, assume token is valid (will error on submit if not)
        if (err.response?.status === 404) {
          setTokenValid(true);
        } else {
          setError(err.response?.data?.error || 'Invalid or expired invitation link');
        }
      } finally {
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!password) {
      setError('Password is required');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      // Try the new set_password endpoint first (for invitation flow)
      const response = await axios.post(
        `${API_BASE_URL}/portal/auth/set_password/`,
        { token, password },
        { withCredentials: true }
      );

      if (response.data.success && response.data.user) {
        // Auto-login successful - store user data and redirect
        localStorage.setItem('portal_user', JSON.stringify(response.data.user));
        setSuccess(true);

        // Redirect to dashboard
        setTimeout(() => {
          window.location.href = '/portal/dashboard';
        }, 1500);
      } else {
        // Password set but not auto-logged in
        setSuccess(true);
        setTimeout(() => {
          navigate('/portal/login', {
            state: { message: 'Password set successfully. Please sign in.' }
          });
        }, 2000);
      }
    } catch (err: any) {
      // If the new endpoint doesn't exist, fall back to old endpoint
      if (err.response?.status === 404) {
        try {
          await axios.post(`${API_BASE_URL}/portal/password/reset_password/`, {
            token,
            password
          });

          setSuccess(true);
          setTimeout(() => {
            navigate('/portal/login', {
              state: { message: 'Password set successfully. Please sign in.' }
            });
          }, 2000);
        } catch (fallbackErr: any) {
          setError(fallbackErr.response?.data?.error || 'Failed to set password. The invitation may have expired.');
        }
      } else {
        setError(err.response?.data?.error || 'Failed to set password. The invitation may have expired.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <div className="portal-set-password">
        <div className="set-password-card">
          <div className="loading-spinner"></div>
          <p>Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="portal-set-password">
        <div className="set-password-card">
          <div className="set-password-header">
            <img src="/rbf-logo.png" alt="Red Bridge Farm" className="set-password-logo" />
            <h1>Invalid Link</h1>
          </div>
          <div className="error-message">
            This invitation link is invalid. Please contact your store manager for a new invitation.
          </div>
          <button
            className="btn-primary"
            onClick={() => navigate('/portal/login')}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="portal-set-password">
        <div className="set-password-card">
          <div className="set-password-header">
            <div className="success-icon">✓</div>
            <h1>Password Set!</h1>
          </div>
          <p className="success-message">
            Your password has been set successfully. Redirecting...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="portal-set-password">
      <div className="set-password-card">
        <div className="set-password-header">
          <img src="/rbf-logo.png" alt="Red Bridge Farm" className="set-password-logo" />
          <h1>Set Your Password</h1>
          <p className="set-password-subtitle">
            Welcome to the Red Bridge Portal! Please create a password to access your account.
          </p>
        </div>

        {email && (
          <div className="email-display">
            <span className="email-label">Account:</span>
            <span className="email-value">{email}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="set-password-form">
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                minLength={8}
                required
                autoFocus
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
            <span className="field-hint">Must be at least 8 characters</span>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Setting Password...' : 'Set Password & Continue'}
          </button>
        </form>

        <div className="set-password-footer">
          <p>
            Already have an account?{' '}
            <a href="/portal/login">Sign in</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PortalSetPassword;
